import {
	cssColors,
	cssColorsDark,
	cssColorsContrast,
	cssColorsDarkContrast,
	colorSchemes,
	colorContrasts,
} from './constants'
import { createLogger } from './log'
import { storage, isWrapper } from './utils'

const log = createLogger('[COLORS]')
const schemes = Object.values(colorSchemes)
const contrasts = Object.values(colorContrasts)

const getCssVars = (variables = {}) =>
	Object.entries(variables)
		.map(([key, color]) => `--${camelToKebabCase(key)}: ${color};`)
		.join('\n')

export const camelToKebabCase = (str) =>
	str.replace(/[A-Z]|[0-9]{1,}/g, (letter) => `-${letter.toLowerCase()}`)

let VARS_STYLESHEET = null
export const setGlobalCSSVars = (variables) => {
	if (!variables) return false
	if (!VARS_STYLESHEET) {
		VARS_STYLESHEET = document.createElement('style')
		VARS_STYLESHEET.classList.add('pronto-colors')
		document.head.appendChild(VARS_STYLESHEET)
	}

	VARS_STYLESHEET.textContent = `
		:root,
		:root *::before,
		:root *::after {
			${getCssVars(variables)}
		}
	`
}

let _elementMap = new Map()
let _elementMapId = 1
export const setCssVarsForElement = (element, variables) => {
	if (!element?.setAttribute || !variables) return
	let data = {}
	if (_elementMap.has(element)) {
		// check if we've already initialized scheme for this element before
		// if so, just grab the data out of the map
		data = _elementMap.get(element)
	} else {
		// need to do the required setup for this element
		const stylesheet = document.createElement('style')
		document.head.appendChild(stylesheet)
		const id = _elementMapId++
		data = { id, attr: `color-scheme-${id}`, stylesheet }
		element.setAttribute(data.attr, '')
	}

	data.stylesheet.textContent = `
		[${data.attr}] {
			${getCssVars(variables)}
		}
	`
}

export const isValidScheme = (scheme) => schemes.includes(scheme)
export const isValidContrast = (contrast) => contrasts.includes(contrast)

export const resolveSchemeAndContrast = (scheme, contrast) => {
	let colorSet = null
	scheme = isValidScheme(scheme) ? scheme : getColorSchemePreference()
	contrast = isValidContrast(contrast) ? contrast : getColorContrastPreference()
	if (scheme === colorSchemes.SYSTEM) {
		scheme = getSystemColorSchemePreference()
	}
	if (contrast === colorContrasts.SYSTEM) {
		contrast = getSystemColorSchemePreference()
	}
	if (scheme === colorSchemes.DARK) {
		colorSet =
			contrast === colorContrasts.HIGH ? cssColorsDarkContrast : cssColorsDark
	} else {
		colorSet = contrast === colorContrasts.HIGH ? cssColorsContrast : cssColors
	}
	return { scheme, contrast, colorSet }
}

export const switchGlobalColorScheme = (initScheme, initContrast) => {
	log('switching global color scheme', { initScheme, initContrast })
	const resolved = resolveSchemeAndContrast(initScheme, initContrast)
	const { colorSet, scheme, contrast } = resolved
	// set the appropriate classes on the <body> element
	const classesToRemove = Array.from(document.body.classList).filter(
		(cls) => cls.startsWith('scheme-') || cls.startsWith('contrast-')
	)
	document.body.classList.remove(...classesToRemove)
	document.body.classList.add(`scheme-${scheme}`, `contrast-${contrast}`)
	EventBus.$emit('colorSchemeChange', scheme)
	EventBus.$emit('colorContrastChange', contrast)
	// set the appropriate variable values on :root
	setGlobalCSSVars(colorSet)
	log('global color scheme set', { scheme, contrast })
}

export const setColorSchemeForElement = (element, scheme, contrast) => {
	const { colorSet } = resolveSchemeAndContrast(scheme, contrast)
	setCssVarsForElement(element, colorSet)
}

export const clearColorSchemeForElement = (element) => {
	const data = _elementMap.get(element)
	if (!data?.stylesheet) return false
	data.stylesheet.textContent = ''
}

export const setColorSchemePreference = (scheme = colorSchemes.LIGHT) => {
	log('setting color scheme preference', scheme)
	if (scheme === null || scheme === colorSchemes.SYSTEM) {
		return storage.remove('pronto_color_scheme')
	}
	return storage.set('pronto_color_scheme', scheme)
}

export const setColorContrastPreference = (
	contrast = colorContrasts.NORMAL
) => {
	log('setting color contrast preference', contrast)
	if (contrast === null || contrast === colorSchemes.SYSTEM) {
		return storage.remove('pronto_color_contrast')
	}
	return storage.set('pronto_color_contrast', contrast)
}

export const getSavedColorSchemePreference = () => {
	return storage.get('pronto_color_scheme')
}

export const getColorSchemePreference = () => {
	const saved = getSavedColorSchemePreference()
	if (saved && isValidScheme(saved)) return saved
	if (isWrapper()) return colorSchemes.LIGHT
	return getSystemColorSchemePreference()
}

export const getSavedColorContrastPreference = () => {
	return storage.get('pronto_color_contrast')
}

export const getColorContrastPreference = () => {
	const saved = getSavedColorContrastPreference()
	if (saved && isValidContrast(saved)) return saved
	return getSystemContrastPreference()
}

export const getSystemColorSchemePreference = () => {
	const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
	if (prefersDark) return colorSchemes.DARK
	return colorSchemes.LIGHT
}

export const getSystemContrastPreference = () => {
	const queries = ['(forced-colors: active)', '(prefers-contrast: more)'] // windows vs macOS
	const prefersContrast = queries.some((q) => window.matchMedia(q).matches)
	if (prefersContrast) return colorContrasts.HIGH
	return colorContrasts.NORMAL
}
